import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IndividualWithJoin } from "./individuals.entity";
import { ReadAllResponse } from "@/interfaces";
import { inviteIndividual, removeIndividualFromOrg, searchIndividuals, updateIndividualStatus } from "./individuals.api";
import { organization_user_status } from "@/models/organization-user.model";
import { uuid } from "@/interfaces";
import type { SettableWorkerContractStatus } from "@shyne/shared/api";
import type { WorkerContractSortKey } from "@/repositories/individuals.repository";

interface IndividualsState {
  individuals: IndividualWithJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: {
    page: number;
    pageSize: number;
    name: string;
    service: string;
    status: string;
    sortKey: WorkerContractSortKey;
    sortOrder: "asc" | "desc";
  };
  inviteOpen: boolean;
  inviteSaving: boolean;
  inviteError: string | null;
  editingModalOpen: boolean;
  editingIndividual: IndividualWithJoin | null;
}

const initialState: IndividualsState = {
  individuals: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    page: 1,
    pageSize: 10,
    name: "",
    service: "",
    status: "",
    sortKey: "name",
    sortOrder: "asc",
  },
  inviteOpen: false,
  inviteSaving: false,
  inviteError: null,
  editingModalOpen: false,
  editingIndividual: null,
};

export const fetchIndividuals = createAsyncThunk<
  ReadAllResponse<IndividualWithJoin>,
  void,
  { state: { individuals: IndividualsState } }
>("individuals/fetch", async (_, { getState }) => {
  const {
    page,
    pageSize,
    name,
    service,
    status,
    sortKey,
    sortOrder,
  } = getState().individuals.filters;

  return await searchIndividuals(page, pageSize, name, service, status as organization_user_status, sortKey, sortOrder);
});

export const removeIndividual = createAsyncThunk<void, string>(
  "individuals/remove",
  async (id) => {
    await removeIndividualFromOrg(id);
  }
);

export const sendIndividualInvite = createAsyncThunk(
  "individuals/sendInvite",
  async (email: string) => await inviteIndividual(email)
);

export const updateIndividual = createAsyncThunk(
  "individuals/updateStatus",
  async ({ orgIndividualId, status }: { orgIndividualId: uuid; status: SettableWorkerContractStatus }) => {
    await updateIndividualStatus(orgIndividualId, status);
  }
);

const individualsSlice = createSlice({
  name: "individuals",
  initialState,
  reducers: {
    setPage(state, { payload }: PayloadAction<number>) {
      state.filters.page = payload;
    },
    setPageSize(state, { payload }: PayloadAction<number>) {
      state.filters.pageSize = payload;
    },
    setName(state, { payload }: PayloadAction<string>) {
      state.filters.name = payload;
    },
    setService(state, { payload }: PayloadAction<string>) {
      state.filters.service = payload;
    },
    setStatus(state, { payload }: PayloadAction<string>) {
      state.filters.status = payload;
    },
    setSortKey(state, { payload }: PayloadAction<WorkerContractSortKey>) {
      if (state.filters.sortKey === payload) {
        state.filters.sortOrder = state.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        state.filters.sortKey = payload;
        state.filters.sortOrder = "asc";
      }
    },
    setInviteOpen(state, { payload }: { payload: boolean }) {
      state.inviteOpen = payload;
      if (!payload) state.inviteError = null;
    },
    setEditingModalOpen(state, { payload }: { payload: boolean }) {
      state.editingModalOpen = payload;
      if (!payload) state.editingIndividual = null;
    },
    setEditingIndividual(state, { payload }: { payload: IndividualWithJoin | null }) {
      state.editingIndividual = payload;
    }
  },
  extraReducers: (b) =>
    b.addCase(fetchIndividuals.pending, (s) => {
      s.loading = true;
    }).addCase(fetchIndividuals.fulfilled, (s, a) => {
      s.loading = false;
      s.individuals = a.payload.data;
      s.total = a.payload.count ?? 0;
    }).addCase(fetchIndividuals.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message ?? "Failed to load individuals";
    }).addCase(removeIndividual.pending, (s) => {
      s.loading = true;
    }).addCase(removeIndividual.fulfilled, (s) => {
      s.loading = false;
    }).addCase(removeIndividual.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message ?? "Failed to remove individual";
    }).addCase(sendIndividualInvite.pending, (s) => {
      s.inviteSaving = true;
      s.inviteError = null;
    }).addCase(sendIndividualInvite.fulfilled, (s) => {
      s.inviteSaving = false;
    }).addCase(sendIndividualInvite.rejected, (s, a) => {
      s.inviteSaving = false;
      s.inviteError = a.error.message ?? "Failed to send invite";
    }).addCase(updateIndividual.pending, (s) => {
      s.loading = true;
    }).addCase(updateIndividual.fulfilled, (s) => {
      s.loading = false;
    }).addCase(updateIndividual.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message ?? "Failed to update individual";
    }),
});

export const {
  setPage,
  setPageSize,
  setName,
  setService,
  setStatus,
  setSortKey,
  setInviteOpen,
  setEditingModalOpen,
  setEditingIndividual,
} = individualsSlice.actions;
export default individualsSlice.reducer;